import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Clock, Check, ChevronLeft, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import { format, parseISO, isSameDay } from "date-fns";
import { track, attribution } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, name: "Session Type" },
  { id: 2, name: "Date & Time" },
  { id: 3, name: "Your Details" },
  { id: 4, name: "Payment" },
];

const CLIENT_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

const formatLocalTime = (date, startTime, endTime) => {
  try {
    const startUtc = new Date(`${date}T${startTime}:00Z`);
    const endUtc = new Date(`${date}T${endTime}:00Z`);
    const fmt = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: CLIENT_TZ,
    });
    const tzAbbr = new Intl.DateTimeFormat(undefined, {
      timeZone: CLIENT_TZ,
      timeZoneName: "short",
    })
      .formatToParts(startUtc)
      .find((p) => p.type === "timeZoneName")?.value || CLIENT_TZ;
    return `${fmt.format(startUtc)} – ${fmt.format(endUtc)} (${tzAbbr})`;
  } catch {
    return null;
  }
};

const BookingFlow = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Rebook flow (client came from a no-show recovery email)
  const [rebook, setRebook] = useState(null); // { booking_id, eligible, days_left, session_type, client_name, client_email, price_eur }

  // Booking data
  const [selectedSessionType, setSelectedSessionType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rebookId = params.get("rebook");
    if (!rebookId) return;
    axios
      .get(`${API}/booking/rebook-eligibility/${rebookId}`)
      .then((res) => {
        const info = { ...res.data, booking_id: rebookId };
        setRebook(info);
        if (info.client_name) setClientName(info.client_name);
        if (info.client_email) setClientEmail(info.client_email);
        if (info.eligible) {
          track("no_show_rebook_opened", { booking_id: rebookId, days_left: info.days_left });
          toast.success(`Welcome back — your replacement session is on the house (${info.days_left} day${info.days_left === 1 ? "" : "s"} left in your free-rebook window).`);
        }
      })
      .catch(() => {
        toast.error("Could not verify your rebook link. Please book a fresh session below.");
      });
  }, []);

  useEffect(() => {
    // Pre-select the same session type when we know the client is doing a free rebook
    if (rebook?.eligible && rebook?.session_type && sessionTypes.length && !selectedSessionType) {
      const preset = sessionTypes.find((s) => s.type === rebook.session_type);
      if (preset) {
        handleSessionTypeSelect(preset);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebook, sessionTypes, selectedSessionType]);

  // Fetch session types
  const loadSessionTypes = async () => {
    setTypesLoading(true);
    setTypesError(false);
    try {
      const response = await axios.get(`${API}/session-types`);
      if (!response.data?.length) throw new Error("empty session types");
      setSessionTypes(response.data);
    } catch (error) {
      console.error("Error fetching session types:", error);
      setTypesError(true);
    } finally {
      setTypesLoading(false);
    }
  };

  const loadSlots = async () => {
    try {
      const response = await axios.get(`${API}/availability/available`);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  useEffect(() => {
    loadSessionTypes();
    loadSlots();
  }, []);

  const retryLoad = () => {
    track("booking_load_retry");
    loadSessionTypes();
    loadSlots();
  };

  // Get unique dates with available slots
  const availableDates = [...new Set(availableSlots.map(slot => slot.date))].map(d => parseISO(d));

  // Chronological list of every open slot — powers the "soonest openings" quick picks
  const sortedSlots = [...availableSlots].sort(
    (a, b) => `${a.date}T${a.start_time}`.localeCompare(`${b.date}T${b.start_time}`)
  );
  const soonestSlots = sortedSlots.slice(0, 3);
  const firstAvailableDate = sortedSlots.length ? parseISO(sortedSlots[0].date) : undefined;
  const lastAvailableDate = sortedSlots.length ? parseISO(sortedSlots[sortedSlots.length - 1].date) : undefined;
  const weeksOfAvailability = lastAvailableDate
    ? Math.max(1, Math.ceil((lastAvailableDate - new Date()) / (7 * 24 * 60 * 60 * 1000)))
    : 0;

  // Show two months side by side on desktop so a busy week doesn't look like no availability
  const [monthsToShow, setMonthsToShow] = useState(typeof window !== "undefined" && window.innerWidth >= 768 ? 2 : 1);
  useEffect(() => {
    const onResize = () => setMonthsToShow(window.innerWidth >= 768 ? 2 : 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Land on the soonest open day so times are visible immediately
  useEffect(() => {
    if (currentStep === 2 && !selectedDate && firstAvailableDate) {
      setSelectedDate(firstAvailableDate);
    }
  }, [currentStep, firstAvailableDate, selectedDate]);

  // Get slots for selected date - sorted chronologically
  const slotsForSelectedDate = selectedDate 
    ? availableSlots
        .filter(slot => isSameDay(parseISO(slot.date), selectedDate))
        .sort((a, b) => a.start_time.localeCompare(b.start_time))
    : [];

  // Auto-advance when session type is selected
  const handleSessionTypeSelect = (type) => {
    setSelectedSessionType(type);
    track("booking_session_type_selected", { session_type: type.type, price: type.price_eur });
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(2);
      setIsTransitioning(false);
    }, 400);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    track("booking_slot_selected", { date: slot.date, start_time: slot.start_time });
  };

  const handleCreateBooking = async () => {
    if (!clientName.trim() || !clientEmail.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      if (rebook?.eligible) {
        const res = await axios.post(`${API}/booking/free-rebook`, {
          slot_id: selectedSlot.id,
          session_type: selectedSessionType.type,
          client_name: clientName.trim(),
          client_email: clientEmail.trim(),
          attribution: attribution(),
          rebook_from: rebook.booking_id,
          client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        });
        setBooking({
          id: res.data.booking_id,
          session_type: selectedSessionType.type,
          date: selectedSlot.date,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          price_eur: 0,
          payment_status: "paid",
          client_name: clientName.trim(),
          client_email: clientEmail.trim(),
        });
        track("no_show_rebook_confirmed", { booking_id: res.data.booking_id });
        toast.success(res.data.message);
        setCurrentStep(4);
        return;
      }

      const response = await axios.post(`${API}/bookings`, {
        slot_id: selectedSlot.id,
        session_type: selectedSessionType.type,
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        attribution: attribution(),
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      });
      setBooking(response.data);
      track("booking_created", {
        booking_id: response.data.id,
        session_type: selectedSessionType.type,
        price: selectedSessionType.price_eur,
      });
      setCurrentStep(4);
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (!booking) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      
      if (response.data.checkout_url) {
        track("booking_checkout_started", { booking_id: booking.id, price: booking.price_eur });
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedSessionType !== null;
      case 2: return selectedSlot !== null;
      case 3: return clientName.trim() !== "" && clientEmail.trim() !== "";
      default: return true;
    }
  };

  const nextStep = () => {
    if (currentStep === 3) {
      handleCreateBooking();
    } else if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="p-6">
      {/* Step Indicators */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center ${idx < STEPS.length - 1 ? 'step-indicator' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
              <div className={`
                w-8 h-8 flex items-center justify-center text-sm font-medium transition-all
                ${currentStep === step.id ? 'bg-[#D4AF37] text-slate-950' : ''}
                ${currentStep > step.id ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]' : ''}
                ${currentStep < step.id ? 'bg-slate-800 text-slate-500' : ''}
              `}>
                {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
              </div>
              <span className={`ml-2 text-xs uppercase tracking-wider hidden sm:block ${
                currentStep >= step.id ? 'text-white' : 'text-slate-500'
              }`}>
                {step.name}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`w-8 md:w-16 h-px mx-2 ${
                currentStep > step.id ? 'bg-[#D4AF37]' : 'bg-slate-800'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {rebook?.eligible && (
          <div
            className="border border-[#D4AF37] bg-[#D4AF37]/[0.08] p-5 mb-6"
            data-testid="rebook-banner"
          >
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-1">Free replacement session</p>
            <p className="text-white text-sm">
              Welcome back. Your session credit is applied — pick a new slot and your total is €0. {rebook.days_left} day{rebook.days_left === 1 ? "" : "s"} left in your rebook window.
            </p>
          </div>
        )}
        {rebook && !rebook.eligible && rebook.reason === "window_expired" && (
          <div
            className="border border-slate-700 bg-slate-900/60 p-5 mb-6"
            data-testid="rebook-expired-banner"
          >
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-1">Rebook window closed</p>
            <p className="text-slate-300 text-sm">
              Your free-rebook window has closed. You can still book a new session below at the regular price.
            </p>
          </div>
        )}
        {rebook && !rebook.eligible && rebook.reason === "already_rebooked" && (
          <div
            className="border border-slate-700 bg-slate-900/60 p-5 mb-6"
            data-testid="rebook-already-banner"
          >
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-1">Already rebooked</p>
            <p className="text-slate-300 text-sm">
              Looks like you've already rebooked this session. Pick another slot below if you'd like an additional one.
            </p>
          </div>
        )}
        {/* Step 1: Session Type */}
        {currentStep === 1 && (
          <div className={`space-y-6 animate-fadeInUp transition-opacity duration-300 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
            <div>
              <h3 className="font-serif text-xl mb-2">Select Session Type</h3>
              <p className="text-slate-400 text-sm">Choose the type of consultation you need.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {typesLoading &&
                [0, 1].map((i) => (
                  <div
                    key={i}
                    data-testid={`session-type-skeleton-${i}`}
                    className="p-6 bg-slate-900/50 border border-slate-800 animate-pulse"
                  >
                    <div className="h-4 w-24 bg-slate-800 mb-4" />
                    <div className="h-5 w-40 bg-slate-800 mb-4" />
                    <div className="h-7 w-20 bg-slate-800" />
                  </div>
                ))}
              {!typesLoading &&
                sessionTypes.map((type) => (
                <button
                  key={type.type}
                  data-testid={`session-type-${type.type}`}
                  onClick={() => handleSessionTypeSelect(type)}
                  disabled={isTransitioning}
                  className={`session-card p-6 text-left bg-slate-900/50 ${
                    selectedSessionType?.type === type.type ? 'selected' : ''
                  } ${isTransitioning ? 'pointer-events-none' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-slate-400 text-sm">{type.duration_minutes} minutes</span>
                  </div>
                  <h4 className="font-serif text-lg text-white mb-2">{type.description}</h4>
                  <p className="price-tag text-[#D4AF37] text-2xl">€{type.price_eur.toFixed(0)}</p>
                  <span className="text-slate-500 text-xs">incl. VAT</span>
                </button>
              ))}
            </div>
            {!typesLoading && typesError && (
              <div
                className="text-center py-10 bg-slate-900/50 border border-slate-800"
                data-testid="session-types-error"
              >
                <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-300 text-sm">We couldn't load the session options just now.</p>
                <p className="text-slate-500 text-xs mt-2 mb-5">This is usually a brief connection hiccup.</p>
                <Button
                  onClick={retryLoad}
                  data-testid="session-types-retry"
                  className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none px-6 py-3 h-auto uppercase tracking-widest text-xs"
                >
                  Try again
                </Button>
              </div>
            )}
            {!typesLoading && !typesError && (
              <p className="text-slate-500 text-xs text-center">Click a session type to continue</p>
            )}
          </div>
        )}

        {/* Step 2: Date & Time */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fadeInUp">
            <div>
              <h3 className="font-serif text-xl mb-2">Select Date & Time</h3>
              <p className="text-slate-400 text-sm">
                {sortedSlots.length > 0
                  ? `${sortedSlots.length} open slots over the next ${weeksOfAvailability} week${weeksOfAvailability === 1 ? "" : "s"} — pick whatever suits you.`
                  : "Choose an available time slot for your consultation."}
              </p>
            </div>

            {soonestSlots.length > 0 && (
              <div className="bg-slate-900/50 border border-slate-800 p-4" data-testid="soonest-slots">
                <p className="text-slate-500 text-[10px] uppercase tracking-[0.2em] mb-3">Soonest openings</p>
                <div className="flex flex-wrap gap-2">
                  {soonestSlots.map((slot) => (
                    <button
                      key={`soonest-${slot.id}`}
                      data-testid={`soonest-slot-${slot.id}`}
                      onClick={() => {
                        setSelectedDate(parseISO(slot.date));
                        handleSlotSelect(slot);
                      }}
                      className={`px-4 py-2 border text-xs transition-colors ${
                        selectedSlot?.id === slot.id
                          ? "border-[#D4AF37] text-[#D4AF37]"
                          : "border-slate-700 text-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {format(parseISO(slot.date), "EEE d MMM")} · {slot.start_time}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {availableSlots.length === 0 ? (
              <div className="text-center py-12 bg-slate-900/50 border border-slate-800">
                <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No available time slots at the moment.</p>
                <p className="text-slate-500 text-sm mt-2">Please check back later or contact us directly.</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-[auto,1fr] gap-6">
                <div className="bg-slate-900/50 border border-slate-800 p-4">
                  <Calendar
                    mode="single"
                    numberOfMonths={monthsToShow}
                    defaultMonth={firstAvailableDate}
                    fromMonth={new Date()}
                    toMonth={lastAvailableDate}
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (date < today) return true;
                      return !availableDates.some(d => isSameDay(d, date));
                    }}
                    modifiers={{
                      available: availableDates
                    }}
                    modifiersStyles={{
                      available: { fontWeight: 'bold' }
                    }}
                    className="rounded-none"
                  />
                </div>

                <div>
                  <h4 className="text-sm uppercase tracking-wider text-slate-400 mb-4">
                    {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Select a date'}
                  </h4>
                  
                  {selectedDate && slotsForSelectedDate.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto pr-2">
                      {slotsForSelectedDate.map((slot) => (
                        <button
                          key={slot.id}
                          data-testid={`time-slot-${slot.id}`}
                          onClick={() => handleSlotSelect(slot)}
                          className={`time-slot px-4 py-3 border border-slate-700 text-sm font-medium ${
                            selectedSlot?.id === slot.id ? 'selected' : 'text-slate-300'
                          }`}
                        >
                          {slot.start_time} - {slot.end_time}
                        </button>
                      ))}
                    </div>
                  ) : selectedDate ? (
                    <p className="text-slate-500 text-sm">No slots available for this date.</p>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Your Details */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-fadeInUp">
            <div>
              <h3 className="font-serif text-xl mb-2">Your Details</h3>
              <p className="text-slate-400 text-sm">Enter your information to confirm the booking.</p>
            </div>

            <div className="max-w-md space-y-4">
              <div>
                <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                <Input
                  id="name"
                  data-testid="client-name-input"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Your full name"
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 h-12 rounded-none mt-2"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="client-email-input"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="your@email.com"
                  readOnly={rebook?.eligible}
                  className={`bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 h-12 rounded-none mt-2 ${
                    rebook?.eligible ? "cursor-not-allowed opacity-80" : ""
                  }`}
                />
                {rebook?.eligible && (
                  <p className="text-slate-500 text-xs mt-2" data-testid="rebook-email-locked-note">
                    Linked to your original booking — cannot be changed.
                  </p>
                )}
              </div>

              {/* Booking Summary */}
              <div className="bg-slate-900/50 border border-slate-800 p-4 mt-6">
                <h4 className="text-sm uppercase tracking-wider text-slate-400 mb-3">Booking Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Session:</span>
                    <span className="text-white">{selectedSessionType?.description}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Date:</span>
                    <span className="text-white">{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Time (UTC):</span>
                    <span className="text-white">{selectedSlot?.start_time} - {selectedSlot?.end_time}</span>
                  </div>
                  {selectedSlot && CLIENT_TZ !== "UTC" && (
                    <div className="flex justify-between text-xs" data-testid="booking-local-time-summary">
                      <span className="text-slate-500">Your local time:</span>
                      <span className="text-slate-300">{formatLocalTime(selectedSlot.date, selectedSlot.start_time, selectedSlot.end_time)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between">
                    <span className="text-slate-400">Total:</span>
                    <span className="text-[#D4AF37] font-medium price-tag">€{selectedSessionType?.price_eur.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Payment / Confirmation */}
        {currentStep === 4 && booking && (
          <div className="space-y-6 animate-fadeInUp text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF37]/20 border border-[#D4AF37] mb-4">
              <Check className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-2">
                {rebook?.eligible ? "Replacement Session Confirmed" : "Booking Created"}
              </h3>
              <p className="text-slate-400">
                {rebook?.eligible
                  ? "Your credit has been applied — nothing to pay. We've emailed the confirmation to you."
                  : "Your booking has been reserved. Complete payment to confirm."}
              </p>
            </div>

            {/* Booking Details */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 max-w-md mx-auto text-left">
              <h4 className="text-sm uppercase tracking-wider text-slate-400 mb-4">Booking Details</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Session:</span>
                  <span className="text-white">{selectedSessionType?.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Date:</span>
                  <span className="text-white">{booking.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Time (UTC):</span>
                  <span className="text-white">{booking.start_time} - {booking.end_time}</span>
                </div>
                {CLIENT_TZ !== "UTC" && (
                  <div className="flex justify-between text-xs" data-testid="booking-local-time-confirm">
                    <span className="text-slate-500">Your local time:</span>
                    <span className="text-slate-300">{formatLocalTime(booking.date, booking.start_time, booking.end_time)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-400">Name:</span>
                  <span className="text-white">{booking.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Email:</span>
                  <span className="text-white">{booking.client_email}</span>
                </div>
                <div className="border-t border-slate-700 pt-3 mt-3 flex justify-between items-center">
                  <span className="text-slate-400">Total:</span>
                  <span
                    className="text-[#D4AF37] text-2xl font-medium price-tag"
                    data-testid="booking-total"
                  >
                    {rebook?.eligible ? "€0.00 · credit applied" : `€${booking.price_eur.toFixed(2)}`}
                  </span>
                </div>
              </div>
            </div>

            {rebook?.eligible ? (
              <Button
                data-testid="close-rebook-btn"
                onClick={onClose}
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none uppercase tracking-widest text-sm btn-primary"
              >
                Done
              </Button>
            ) : (
              <Button
                data-testid="proceed-to-payment-btn"
                onClick={handleProceedToPayment}
                disabled={loading}
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none uppercase tracking-widest text-sm btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Proceed to Payment"
                )}
              </Button>
            )}

            {!rebook?.eligible && (
              <p className="text-slate-500 text-xs max-w-md mx-auto leading-relaxed" data-testid="booking-merchant-note">
                Payment is handled securely by Stripe on behalf of Apex Advisory. Your card statement shows a discreet
                reference (LEADERSHIP) — never the clinic name.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {currentStep < 4 && currentStep > 1 && (
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
          <Button
            variant="ghost"
            onClick={prevStep}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Button
            data-testid="next-step-btn"
            onClick={nextStep}
            disabled={!canProceed() || loading}
            className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none px-6 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {currentStep === 3 ? 'Create Booking' : 'Continue'}
                <ChevronRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Cancel button only for step 1 */}
      {currentStep === 1 && (
        <div className="flex justify-start mt-8 pt-6 border-t border-slate-800">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default BookingFlow;
